import { Utilities } from '../../types';
import { OptionTypeWithCode } from '../Select/types';
import React, { CSSProperties } from 'react';
import type { Table, ColumnResizeDirection, ColumnResizeMode, Row } from '@tanstack/react-table';
import { IconName } from '../Icon';

export interface DataTableProps<
  DATA_MODEL extends DataTableTypes.DataModel,
  QUERY_MODEL extends DataTableTypes.QueryModel = DataTableTypes.QueryModel,
  SORT_MODEL extends DataTableTypes.SortModel = DataTableTypes.SortModel
> {
  /**
   * DATA
   */
  data: DATA_MODEL[];
  /**
   * preview - hides toolbar, thead cells are grey, shows vertical borders on cells
   * @default default
   */
  variant?: DataTableTypes.TableVariant;

  /**
   * TABLE MODEL a.k.a. Most likely query variables
   */
  model?: DataTableTypes.InputTableModel<QUERY_MODEL, SORT_MODEL>;
  // previous: onVariablesChanged
  onModelChange?: DataTableTypes.OnModelChange<QUERY_MODEL, SORT_MODEL>;
  debounceModelChange?: number;
  requestOnInit?: boolean;
  /**
   * COLUMN / COLUMN CONFIGURATION
   */
  columns: DataTableTypes.ColumnDefinition<DATA_MODEL, QUERY_MODEL, SORT_MODEL>[];

  // previous: initialState
  initialColumnConfiguration?: DataTableTypes.ColumnConfiguration<DATA_MODEL> | undefined;
  // previous: onColumnConfigurationChanged
  onColumnConfigurationChange?(configuration: DataTableTypes.ColumnConfiguration<DATA_MODEL>): void;

  /**
   * UX
   */

  loading?: boolean; // previous: isFetching
  loadingInBackground?: boolean; // previous: isFetching
  resizable?: boolean;
  expandable?: DataTableTypes.ExpandableKey<DATA_MODEL> | ((model: DATA_MODEL) => any[] | undefined);
  selectable?: DataTableTypes.SelectableKey<DATA_MODEL>;

  slotProps?: {
    filter?: {
      elements: DataTableTypes.Filters.Generator<QUERY_MODEL>;
    };
    toolbar?: {
      search?:
        | boolean
        | {
            disabled?: boolean;
            placeholder?: string;
            dataTest?: string;
          };
      addButton?: {
        dataTest?: string;
        disabled?: boolean;
        label?: string;
        iconName?: IconName;
        onClick?: () => void;
      };
      beforeActions?: React.ReactNode;
      afterActions?: React.ReactNode;
    };
    bulkToolbar?: {
      beforeActions?: React.ReactNode;
      afterActions?: React.ReactNode;
      actions: (
        | { type: 'custom'; key: string; render: (selectedRows: DATA_MODEL[]) => React.ReactNode }
        | { type: 'separator' }
        | {
            type: 'action';
            label: string;
            iconName?: IconName;
            disabled?: boolean;
            tooltipText?: string;
            dataTest?: string;
            disabledMessage?: string;
            onClick: (selectedRows: DATA_MODEL[]) => void;
          }
      )[];
    };
    expandableRow?: {
      render: (data: DATA_MODEL) => React.ReactNode;
    };
  };

  slots?: {
    header?: React.ReactNode;
    filter?: {
      toolbar?: {
        activeFilters?: React.ReactNode;
      };
      drawer?: {
        subheader?: React.ReactNode;
        activeFiltersActions?: React.ReactNode;
      };
    };
    bulkToolbar?: React.ReactNode;
  };
}

export namespace DataTableTypes {
  export interface MergeModels<
    DATA_MODEL extends DataModel,
    QUERY_MODEL extends QueryModel = QueryModel,
    SORT_MODEL extends SortModel = SortModel
  > {
    TableModel: InputTableModel<QUERY_MODEL, SORT_MODEL>;
    Column: ColumnDefinition<DATA_MODEL, QUERY_MODEL, SORT_MODEL>;
    DataModel: DATA_MODEL;
    QueryModel: QUERY_MODEL;
    SortModel: SORT_MODEL;
  }

  export type TableInstance<DATA_MODEL extends DataModel = DataModel> = Table<DATA_MODEL>;
  /**
   * DATA
   */
  export type DataModel = object;
  export type QueryModel = Record<string, unknown> & { search?: string };
  export type SortModel = string;

  export type PageModelQuery = {
    offset: number;
    limit: number;
    totalCount: number;
  };

  export type SortModelQuery<SORT_MODEL extends SortModel> = { type: SORT_MODEL; reversed: boolean };

  export interface TableModel<QUERY_MODEL extends QueryModel, SORT_MODEL extends SortModel = SortModel> {
    query: QUERY_MODEL;
    page: Omit<PageModelQuery, 'totalCount'>;
    sort: SortModelQuery<SORT_MODEL>[] | undefined;
  }

  export interface InputTableModel<QUERY_MODEL extends QueryModel, SORT_MODEL extends SortModel = SortModel> {
    query?: QUERY_MODEL;
    page?: Partial<PageModelQuery>;
    sort?: SortModelQuery<SORT_MODEL>[] | undefined;
  }

  export type OnModelChange<QUERY_MODEL extends QueryModel, SORT_MODEL extends SortModel = SortModel> = (
    model: DataTableTypes.TableModel<QUERY_MODEL, SORT_MODEL>
  ) => void;

  /**
   * COLUMN
   */

  type FalsyValue = '' | undefined | null | false;
  type CellFunction<DATA_MODEL extends DataModel> = (
    model: DATA_MODEL,
    row: Pick<Row<DATA_MODEL>, 'depth'>
  ) => React.ReactNode | FalsyValue;
  type DataTestFunction<DATA_MODEL extends DataModel> = (model: DATA_MODEL | undefined) => string;

  export interface MenuColumn<DATA_MODEL extends DataModel> {
    type: 'menu';
    dataTest?: string | DataTestFunction<DATA_MODEL>;
    visibility?: {
      cellStyles?: CSSProperties;
    };
    hidden?: boolean | ((model: DATA_MODEL) => boolean);
    actions?: {
      label: string | ((model: DATA_MODEL) => string);
      onClick: (model: DATA_MODEL) => void;
      disabled?: boolean | ((model: DATA_MODEL) => boolean);
      hidden?: boolean | ((model: DATA_MODEL) => boolean);
      disabledMessage?: string;
      iconName?: IconName;
      dataTest?: string;
      red?: boolean;
    }[];
  }

  export type ColumnDefinition<
    DATA_MODEL extends DataModel,
    QUERY_MODEL extends QueryModel = QueryModel,
    SORT_MODEL extends SortModel = SortModel
  > =
    | {
        id: Exclude<(keyof DATA_MODEL & string) | string, 'menu'>;
        type?: 'column';
        Header: string | React.ReactNode;
        Cell: CellFunction<DATA_MODEL>;
        CellBeforeContent?: CellFunction<DATA_MODEL>;
        CellAfterContent?: CellFunction<DATA_MODEL>;
        dataTest?: string | DataTestFunction<DATA_MODEL>;
        visibility?: {
          align?: 'left' | 'center' | 'right';
          width?: number;
          minWidth?: number;
          maxWidth?: number;
          // TODO
          ellipsisOnOverflow?: boolean;
          blockColumnHiding?: boolean;
          cellStyles?: CSSProperties;
          cellWrapperStyle?: (model: DATA_MODEL) => CSSProperties | undefined;
        };
        sort?: SORT_MODEL;
        filter?: Partial<{
          [prop in keyof QUERY_MODEL]: {
            options: { label: string; value: Exclude<QUERY_MODEL[prop], undefined | null> }[];
            onChange(option: QUERY_MODEL[prop][] | undefined): void;
            value: QUERY_MODEL[prop][] | undefined;
          };
        }>;
      }
    /**
     * Use type: 'menu' to define menu items
     */
    | MenuColumn<DATA_MODEL>;

  export interface ColumnConfiguration<DATA extends DataModel> {
    hiddenColumns?: ((keyof DATA & string) | string)[] | undefined;
    columnOrder?: ((keyof DATA & string) | string)[];
    columnSizing?: Record<(keyof DATA & string) | string, number>;
  }

  /**
   * UI
   */

  export type TableVariant = 'preview' | 'default';
  export type SelectableKey<DATA_MODEL extends DataModel> = Utilities.KeyOfType<DATA_MODEL, string>;
  export type ExpandableKey<DATA_MODEL extends DataModel> = Utilities.KeyOfType<DATA_MODEL, any[]>;

  export interface SelectedRow<DATA_MODEL extends DataModel> {
    id: string;
    data: DATA_MODEL;
  }

  /**
   * DERIVED STATE
   */

  export interface DerivedState<DATA_MODEL extends DataModel> {
    ui: {
      variant: DataTableTypes.TableVariant;
      show: {
        toolbar: boolean;
      };
    };
    state: {
      loading: boolean;
      loadingInBackground: boolean;
    };
    features: {
      selectable:
        | {
            enabled: true;
            key: DataTableTypes.SelectableKey<DATA_MODEL>;
          }
        | { enabled: false; key: undefined };
      expandable:
        | {
            enabled: true;
            key: DataTableTypes.ExpandableKey<DATA_MODEL> | ((model: DATA_MODEL) => any[] | undefined);
          }
        | { enabled: false; key: undefined };
      columnManagement: {
        enabled: boolean;
        resizable:
          | {
              enabled: true;
              mode: ColumnResizeMode;
              direction: ColumnResizeDirection;
            }
          | {
              enabled: false;
              mode: undefined;
              direction: undefined;
            };
      };
    };
    pageModel: {
      totalCount: number;
      limit: number;
      offset: number;
      currentPage: number;
    };
  }

  export namespace Filters {
    export type Generator<QUERY_MODEL extends QueryModel> = (payload: {
      queryModel: Partial<QUERY_MODEL>;
      setQueryModel: <K extends Utilities.Leaves<QUERY_MODEL>>(
        key: K,
        value: Utilities.LeafType<QUERY_MODEL, K>
      ) => void;
    }) => DataTableTypes.Filters.FilterDefinition<QUERY_MODEL>[];

    export enum Type {
      multiSelect = 'multiSelect',
      singleSelect = 'singleSelect',
      singleChipSelect = 'singleChipSelect',
      multiChipSelect = 'multiChipSelect',
      input = 'input',
      date = 'date',
      dateRange = 'dateRange',
      // dateRangeRelative = 'dateRangeRelative', // TO MIGRATE
      custom = 'custom',
      switch = 'switch',
      hidden = 'hidden'
    }

    export type FilterDefinition<QUERY_MODEL extends QueryModel> =
      | DataTableSingleSelectFilter<QUERY_MODEL>
      | DataTableMultiSelectFilter<QUERY_MODEL>
      | DataTableSingleChipSelectFilter<QUERY_MODEL>
      | DataTableMultiChipSelectFilter<QUERY_MODEL>
      | DataTableSwitchFilter<QUERY_MODEL>
      | DataTableCustomFilter<QUERY_MODEL>
      | DataTableInputFilter<QUERY_MODEL>
      | DataTableHiddenFilter<QUERY_MODEL>
      | DataTableDateFilter<QUERY_MODEL>
      | DataTableDateRangeFilter<QUERY_MODEL>;
    // | DataTableRelativeDateRangeFilter<QUERY_MODEL>

    export interface DataTableSingleSelectFilter<QUERY_MODEL extends QueryModel> {
      type: Type.singleSelect;
      id: Exclude<
        Utilities.Leaves<QUERY_MODEL>,
        Utilities.LeavesOfType<QUERY_MODEL, any[]> | Utilities.LeavesOfType<QUERY_MODEL, boolean>
      > &
        string;
      options: OptionTypeWithCode<string | Record<string, unknown>>[];
      onSearch?: (search: string) => void;
      onInit?: (uid: string) => void;
      autoComplete?: boolean;
      loading?: boolean;
      label: string;
      placeholder?: string;
    }

    export interface DataTableMultiSelectFilter<QUERY_MODEL extends QueryModel> {
      type: Type.multiSelect;
      id: Utilities.LeavesOfType<QUERY_MODEL, any[]> & string;
      options: OptionTypeWithCode<string | Record<string, unknown>>[];
      onSearch?: (search: string) => void;
      onInit?: (uids: string[]) => void;
      autoComplete?: boolean;
      loading?: boolean;
      label: string;
      placeholder?: string;
    }

    export interface DataTableSingleChipSelectFilter<QUERY_MODEL extends QueryModel> {
      type: Type.singleChipSelect;
      id: Utilities.LeavesOfType<QUERY_MODEL, string> & string;
      options: {
        label: string;
        value: string;
        iconName?: IconName;
      }[];
      label: string;
    }

    export interface DataTableMultiChipSelectFilter<QUERY_MODEL extends QueryModel> {
      type: Type.multiChipSelect;
      id: Utilities.LeavesOfType<QUERY_MODEL, string[]> & string;
      options: {
        label: string;
        value: string;
        iconName?: IconName;
      }[];
      label: string;
    }

    export interface DataTableSwitchFilter<QUERY_MODEL extends QueryModel> {
      type: Type.switch;
      id: Utilities.LeavesOfType<Utilities.DeeplyDefined<QUERY_MODEL>, boolean>;
      label: string;
      undefinedOnFalse?: boolean;
    }

    export interface DataTableCustomFilter<QUERY_MODEL extends QueryModel> {
      type: Type.custom;
      id: Utilities.Leaves<Utilities.DeeplyDefined<QUERY_MODEL>>;
      label: string;
      component: React.ReactNode;
    }

    export interface DataTableInputFilter<QUERY_MODEL extends QueryModel> {
      type: Type.input;
      id: Utilities.LeavesOfType<Utilities.DeeplyDefined<QUERY_MODEL>, string | number>;
      label: string;
      placeholder?: string;
      startIcon?: IconName;
      numberProps?: {
        min?: number;
        max?: number;
        step?: number;
      };
      inputType?: 'text' | 'number' | 'url' | 'email';
    }

    export interface DataTableHiddenFilter<QUERY_MODEL extends QueryModel> {
      type: Type.hidden;
      id: Utilities.LeavesOfType<Utilities.DeeplyDefined<QUERY_MODEL>, string | number>;
      label: string;
    }

    export interface DataTableDateFilter<QUERY_MODEL extends QueryModel> {
      type: Type.date;
      id: Utilities.LeavesOfType<Utilities.DeeplyDefined<QUERY_MODEL>, Date | string | number>;
      label: string;
      placeholder?: string;
      disablePast?: boolean;
      disableFuture?: boolean;
    }

    export interface DataTableDateRangeFilter<QUERY_MODEL extends QueryModel> {
      type: Type.dateRange;
      id: Utilities.LeavesOfType<Utilities.DeeplyDefined<QUERY_MODEL>, Date | string | number>;
      endDateKey: Utilities.LeavesOfType<Utilities.DeeplyDefined<QUERY_MODEL>, Date | string | number>;
      label: string;
      dateTime?: boolean;
    }

    // export interface DataTableRelativeDateRangeFilter<QUERY_MODEL extends QueryModel> {
    //   type: Type.dateRangeRelative;
    //   id: KeyOfType<QUERY_MODEL, { amount: number; from: string; offset: string; }>;
    //   label: string;
    //   excludedTypes?: DateTypeModel[];
    //   dateTime?: boolean;
    // }
  }
}
